import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import FocusLayout from '../components/layouts/FocusLayout';
import { logout, isUserNotFoundError } from '../utils/auth';

const FullTestSummaryPage = () => {
    const { attemptId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [attempt, setAttempt] = useState(location.state?.attempt || null);
    const [isGrading, setIsGrading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (attempt) return;

        const fetchAttempt = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                if (!token) throw new Error('Authentication token not found.');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attemptId}`, {
                    headers: { 'accept': 'application/json', 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    if (isUserNotFoundError(response, errorData)) {
                        logout(navigate);
                        return;
                    }
                    throw new Error(errorData.detail || 'Failed to fetch attempt details');
                }
                const data = await response.json();
                setAttempt(data);
            } catch (err) {
                setError(err.message);
            }
        };

        fetchAttempt();
    }, [attemptId, attempt, navigate]);

    const submitExamAttempt = async () => {
        setIsGrading(true);
        try {
            const token = localStorage.getItem('accessToken');
            if (!token) throw new Error('Authentication token not found.');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/exam-attempts/${attemptId}/submit`, {
                method: 'POST',
                headers: {
                    'accept': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: ''
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                if (isUserNotFoundError(response, errorData)) {
                    logout(navigate);
                    return;
                }
                throw new Error(errorData.detail || 'Failed to submit exam attempt');
            }
            const data = await response.json();
            localStorage.removeItem(`attempt_${attemptId}`);
            navigate(`/full-mock-test/result/${attemptId}`, { state: { result: data } });
        } catch (err) {
            setError(err.message);
            setIsGrading(false);
        }
    };

    if (error) return <div className="p-10 text-red-500">Error: {error}</div>;
    if (!attempt) return <div className="p-10">Loading...</div>;

    // Calculate summary stats
    let totalQuestions = 0;
    attempt.exam_structure.forEach(s => totalQuestions += s.exercises.length);

    return (
        <FocusLayout>
            <div className="min-h-screen bg-[#F8F6F0] p-4 md:p-10 hp-trail-pattern">
                {isGrading && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white/90 backdrop-blur-sm">
                        <div className="text-center max-w-md px-6">
                            <div className="w-24 h-24 rounded-3xl bg-hupath-scout/10 flex items-center justify-center animate-pulse mb-8 mx-auto">
                                <span className="material-symbols-outlined text-5xl text-hupath-scout">assignment_turned_in</span>
                            </div>
                            <h2 className="text-3xl font-display font-black text-slate-800 mb-4">Giáo viên đang chấm bài...</h2>
                            <p className="text-slate-500 mb-8">Vui lòng chờ trong giây lát. Hệ thống AI đang phân tích bài làm của em.</p>
                            <div className="w-full bg-slate-100 rounded-full h-2 mb-2 overflow-hidden relative">
                                <div className="absolute top-0 bottom-0 bg-hupath-scout rounded-full animate-loading-bar" style={{ width: '60%' }} />
                            </div>
                        </div>
                    </div>
                )}

                <div className="max-w-3xl mx-auto space-y-8 relative z-10 pt-10">
                    <div className="text-center space-y-4">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-hupath-gold/10 rounded-3xl rotate-3 mb-2">
                            <span className="material-symbols-outlined text-5xl text-hupath-gold" style={{ fontVariationSettings: "'FILL' 1" }}>flag</span>
                        </div>
                        <h1 className="text-4xl font-display font-black text-slate-800">Sẵn sàng nộp bài chưa?</h1>
                        <p className="text-slate-500 font-medium italic">Em đã hoàn thành tất cả các phần thi của "{attempt.title}"</p>
                    </div>

                    <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-soft border border-white">
                        <h2 className="text-xl font-display font-black text-slate-800 mb-6 flex items-center gap-2">
                            <span className="material-symbols-outlined text-hupath-scout">fact_check</span>
                            Tóm tắt bài làm
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {attempt.exam_structure.map((section, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 rounded-2xl bg-slate-50 border border-slate-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-slate-400 font-bold shadow-sm">
                                            {idx + 1}
                                        </div>
                                        <span className="font-bold text-slate-700">{section.section_title}</span>
                                    </div>
                                    <span className="text-xs font-black text-hupath-scout bg-hupath-scout/10 px-3 py-1 rounded-full uppercase">
                                        {section.exercises.length} Exercises
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-10 p-6 rounded-2xl bg-amber-50 border border-amber-100 flex gap-4">
                            <span className="material-symbols-outlined text-amber-500">info</span>
                            <p className="text-sm text-amber-800 font-medium leading-relaxed">
                                Sau khi nhấn nộp bài, em sẽ không thể quay lại chỉnh sửa câu trả lời. AI sẽ chấm điểm và nhận xét chi tiết cho từng câu hỏi.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex-1 py-5 px-8 bg-white text-slate-500 font-display font-black rounded-2xl border-2 border-slate-100 hover:bg-slate-50 transition-all"
                        >
                            Xem lại bài làm
                        </button>
                        <button
                            onClick={submitExamAttempt}
                            className="flex-[1.5] py-5 px-8 hp-button-gradient text-white font-display font-black rounded-2xl shadow-lg shadow-hupath-scout/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-3"
                        >
                            <span>Nộp bài & Xem kết quả 🚀</span>
                        </button>
                    </div>
                </div>
            </div>
            <style>{`
                .hp-trail-pattern {
                    background-image: radial-gradient(#22C55E 0.5px, transparent 0.5px);
                    background-size: 24px 24px;
                }
                @keyframes loading-bar {
                    0% { left: -100%; width: 100%; }
                    100% { left: 100%; width: 100%; }
                }
                .animate-loading-bar {
                    animation: loading-bar 2s infinite linear;
                }
            `}</style>
        </FocusLayout>
    );
};

export default FullTestSummaryPage;
